import { engine } from "..";
import { trackLiveObjects } from "../helpers/trackLiveObjects";
import { withDelete } from "../helpers/withDelete";
import { toArray } from "../vector";
import { buildProduct } from "./buildProduct";
import { buildProductCategories } from "./buildProductCategory";
import { buildProductEntries } from "./buildProductEntries";
import { buildSearchResults } from "./buildSearchResults";
import { buildSubProducts } from "./buildSubProducts";
import { QualifiedId } from "./types";

const cachedTechnicalCategories: {
  batch: string[] | null;
  technical: string[] | null;
} = {
  batch: null,
  technical: null,
};

export const productRepository = trackLiveObjects("productRepository", {
  get(qualifiedIds: QualifiedId[]) {
    return qualifiedIds
      .map((qualifiedId) => productRepository.getById(qualifiedId))
      .sort((a, b) => a.qualifiedId.id.localeCompare(b.qualifiedId.id));
  },

  getByCategory(categoryId: string, showDeleted: boolean) {
    const repository = new engine.ProductRepository("direct");
    return withDelete(
      repository,
      buildProductEntries(
        repository.getProductEntries(categoryId, showDeleted),
      ),
    );
  },

  getById(qualifiedId: QualifiedId) {
    const { id, productSetId, type } = qualifiedId;
    const repository = new engine.ProductRepository("direct");
    const engineId = productSetId
      ? new engine.QualifiedId(id, type, productSetId)
      : new engine.QualifiedId(id, type);

    return withDelete(
      [repository, engineId],
      buildProduct(repository.getProductDetails(engineId), qualifiedId),
    );
  },

  getCategories(showDeleted: boolean) {
    const repository = new engine.ProductRepository("direct");
    return withDelete(
      repository,
      buildProductCategories(repository.getProductCategories(showDeleted)),
    );
  },

  getSelectionCategories(selection: "batch" | "technical") {
    const repository = new engine.ProductRepository("direct");
    if (cachedTechnicalCategories[selection]) {
      return withDelete(repository, cachedTechnicalCategories[selection]);
    }
    cachedTechnicalCategories[selection] = withDelete(
      repository,
      toArray(repository.getCategoriesForSelection(selection)).map((category) =>
        category.toString(),
      ),
    );
    return cachedTechnicalCategories[selection];
  },

  getSubProducts(productId: string, showDeleted: boolean) {
    const repository = new engine.ProductRepository("direct");
    return withDelete(
      repository,
      buildSubProducts(repository.getSubProducts(productId, showDeleted)),
    );
  },

  search(query: string, showDeleted: boolean) {
    const repository = new engine.ProductRepository("direct");
    return withDelete(
      repository,
      buildSearchResults(
        repository.searchProducts(query, showDeleted),
        showDeleted,
      ),
    );
  },
});
